/**
 * Popover Component -- V1-inspired premium floating panel
 *
 * A popover displays content in a floating panel anchored to a trigger.
 * Built on Radix UI Popover primitive.
 *
 * Upgrades: rounded-lg, layered shadow with teal tint in dark mode,
 * smoother slide-in distance, consistent animation timing.
 */
import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";

import { cn } from "@/lib/utils";
import { OVERLAY_SURFACE_BASE, OVERLAY_ANIMATION_BASE } from "./style-tokens";

/** Root popover component */
const Popover = PopoverPrimitive.Root;

/** Trigger that opens the popover */
const PopoverTrigger = PopoverPrimitive.Trigger;

/** Anchor for positioning without a trigger */
const PopoverAnchor = PopoverPrimitive.Anchor;

/** Content displayed inside the popover -- elevated panel with premium depth */
const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(({ className, align = "center", sideOffset = 6, ...props }, ref) => (
  <PopoverPrimitive.Portal>
    <PopoverPrimitive.Content
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      className={cn(
        "w-72 p-4 outline-none",
        OVERLAY_SURFACE_BASE,
        OVERLAY_ANIMATION_BASE,
        className
      )}
      {...props}
    />
  </PopoverPrimitive.Portal>
));
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

export { Popover, PopoverTrigger, PopoverContent, PopoverAnchor };
